import { useState, useEffect } from 'react';
import { Room, Resident } from '../types';
import {
  findResidentsWithDiscrepancies,
  getCorrectPaymentDate,
  findMissingLatePayments,
  isActuallyPastDue
} from '../utils/paymentChecker';
import { formatDateDDMMYYYY } from '../utils/format';

interface PaymentDiscrepancyCheckerProps {
  rooms: Room[];
  onUpdateResident: (residentId: string, updates: Partial<Resident>) => Promise<boolean>;
}

export function PaymentDiscrepancyChecker({ rooms, onUpdateResident }: PaymentDiscrepancyCheckerProps) {
  const [discrepancies, setDiscrepancies] = useState<Resident[]>([]);
  const [missingLatePayments, setMissingLatePayments] = useState<Resident[]>([]);
  const [isFixing, setIsFixing] = useState(false);
  const [fixed, setFixed] = useState(0);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    // Find all payment date discrepancies
    const residentsWithDiscrepancies = findResidentsWithDiscrepancies(rooms);
    setDiscrepancies(residentsWithDiscrepancies);

    // Find all residents that should be in late payments but aren't
    const missing = findMissingLatePayments(rooms);
    setMissingLatePayments(missing);
  }, [rooms]);

  const handleFixDiscrepancies = async () => {
    setIsFixing(true);
    setFixed(0);
    setErrors([]);
    
    // Fix all discrepancies
    for (const resident of discrepancies) {
      const correctDate = getCorrectPaymentDate(resident);
      
      if (correctDate) {
        try {
          const success = await onUpdateResident(resident.id, { 
            nextPaymentDate: correctDate 
          });
          
          if (success) {
            setFixed(prevFixed => prevFixed + 1);
          } else {
            setErrors(prev => [...prev, `Failed to update ${resident.name} (Room ${resident.roomNumber})`]);
          }
        } catch (err) {
          setErrors(prev => [...prev, `Error updating ${resident.name}: ${err instanceof Error ? err.message : String(err)}`]);
        }
      }
    }
    
    setIsFixing(false);
  };

  if (discrepancies.length === 0 && missingLatePayments.length === 0) {
    return (
      <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg p-4 mb-4">
        <h2 className="text-lg font-bold text-green-700 dark:text-green-300">Tidak ada ketidaksesuaian tanggal pembayaran</h2>
        <p className="text-sm text-green-600 dark:text-green-400">Semua tanggal pembayaran tercatat dengan benar.</p>
      </div>
    );
  }

  return (
    <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4 mb-4">
      <h2 className="text-lg font-bold text-yellow-700 dark:text-yellow-300">
        {discrepancies.length > 0 ? 
          `Ada ${discrepancies.length} penghuni dengan ketidaksesuaian tanggal pembayaran` : 
          'Ketidaksesuaian Data Pembayaran'}
      </h2>
      
      {missingLatePayments.length > 0 && (
        <p className="text-sm text-yellow-600 dark:text-yellow-400 mb-4">
          Ada {missingLatePayments.length} penghuni yang seharusnya terdaftar dalam daftar tertunggak, 
          namun tidak muncul karena ketidaksesuaian data.
        </p>
      )}
      
      <div className="max-h-96 overflow-y-auto my-3">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-yellow-300 dark:border-yellow-700">
              <th className="py-2 px-3 text-left">Kamar</th>
              <th className="py-2 px-3 text-left">Nama</th>
              <th className="py-2 px-3 text-left">Tanggal Jatuh Tempo Saat Ini</th>
              <th className="py-2 px-3 text-left">Tanggal Jatuh Tempo Seharusnya</th>
              <th className="py-2 px-3 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {discrepancies.map(resident => {
              const correctDate = getCorrectPaymentDate(resident);
              const isPastDue = isActuallyPastDue(resident);
              
              return (
                <tr key={resident.id} className="border-b border-yellow-200 dark:border-yellow-800">
                  <td className="py-2 px-3">{resident.roomNumber}</td>
                  <td className="py-2 px-3">{resident.name}</td>
                  <td className="py-2 px-3">{formatDateDDMMYYYY(resident.nextPaymentDate)}</td>
                  <td className="py-2 px-3">{formatDateDDMMYYYY(correctDate)}</td>
                  <td className={`py-2 px-3 ${isPastDue ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                    {isPastDue ? 'Terlambat' : 'Tepat Waktu'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {errors.length > 0 && (
        <div className="mt-3 p-3 bg-red-100 dark:bg-red-900/50 border border-red-300 dark:border-red-700 rounded">
          <h3 className="font-bold text-red-700 dark:text-red-300 mb-2">Terjadi kesalahan:</h3>
          <ul className="list-disc list-inside text-sm text-red-600 dark:text-red-400">
            {errors.map((error, i) => (
              <li key={i}>{error}</li>
            ))}
          </ul>
        </div>
      )}
      
      <div className="mt-4 flex gap-3">
        <button
          onClick={handleFixDiscrepancies}
          disabled={isFixing || discrepancies.length === 0}
          className={`px-4 py-2 rounded font-medium ${
            isFixing 
              ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed' 
              : 'bg-yellow-500 dark:bg-yellow-600 text-white hover:bg-yellow-600 dark:hover:bg-yellow-700'
          }`}
        >
          {isFixing ? `Memperbaiki... (${fixed}/${discrepancies.length})` : 'Perbaiki Semua Ketidaksesuaian'}
        </button>
      </div>
    </div>
  );
}